/**
 * Zero-quantity placeholder rows for the native cart page's order grid.
 *
 * The cart page renders products in an order grid (snippets/order-grid.liquid): one <tr
 * class="order-grid__row"> per colour/product, a quantity <input class="order-grid__input"> per
 * size wrapped in <order-grid-quantity-selector variant-id="…">, and a per-row remove button
 * (.order-grid__remove-btn inside <order-grid-row-remove variant-ids="…">).
 *
 * When every size of a product is set to 0 the product leaves the Shopify cart and the grid — which
 * re-renders from a server-rendered section containing only in-cart products — drops the whole row.
 * This module keeps that product visible as a placeholder row (all sizes 0, total 0) that the
 * customer can type a quantity back into to re-add it.
 *
 * Behaviour:
 *   - Quantity → 0 (all sizes of a row): the product is really removed from the cart, but we
 *     remember the row in sessionStorage and re-inject it as a placeholder after the grid re-renders.
 *   - Remove button (×): truly removes — the product is dropped AND any stored placeholder for it is
 *     cleared, so it does not come back.
 *   - Re-add: the placeholder row is a live clone with working <order-grid-quantity-selector>
 *     elements, so typing a quantity flows through the grid's own add path; the placeholder is then
 *     dropped because the product is back in the cart.
 *
 * A single static sessionStorage key is used (the Shopify cart token rotates on every mutation, so
 * a token-scoped key would lose entries). Recording is synchronous so it wins the race with the
 * grid's immediate AJAX re-render.
 */
;(function () {
  const STORAGE_KEY = 'cart-zero-rows'

  // Set `window.__cartZeroDebug = true` in the console to trace record/restore steps.
  function debug() {
    if (window.__cartZeroDebug) {
      console.log('[cart-zero]', ...arguments)
    }
  }

  // ---------- sessionStorage ----------

  function readStore() {
    try {
      const raw = window.sessionStorage.getItem(STORAGE_KEY)
      const parsed = raw ? JSON.parse(raw) : null
      return Array.isArray(parsed) ? parsed : []
    } catch (e) {
      return []
    }
  }

  function writeStore(entries) {
    try {
      if (!entries.length) {
        window.sessionStorage.removeItem(STORAGE_KEY)
      } else {
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entries))
      }
    } catch (e) {
      /* storage unavailable — placeholders just won't persist */
    }
  }

  /** Upsert a placeholder entry, keyed by its sorted variant-id list. */
  function rememberPlaceholder(entry) {
    if (!entry.variantIds || !entry.variantIds.length) {
      return
    }
    const key = entry.variantIds.join(',')
    const next = readStore().filter((e) => (e.variantIds || []).join(',') !== key)
    next.push(entry)
    writeStore(next)
  }

  /** Forget any placeholder that references ANY of the given variant ids. */
  function forgetPlaceholdersForVariants(variantIds) {
    const drop = new Set(variantIds.map(String))
    const kept = readStore().filter((e) => !(e.variantIds || []).some((id) => drop.has(String(id))))
    writeStore(kept)
  }

  // ---------- Helpers ----------

  function inCartDrawer(node) {
    return Boolean(node && node.closest && node.closest('#cart-drawer'))
  }

  /** All variant ids for a grid row (from its size cells). */
  function rowVariantIds(row) {
    const ids = []
    row.querySelectorAll('order-grid-quantity-selector[variant-id]').forEach((sel) => {
      const id = sel.getAttribute('variant-id')
      if (id) {
        ids.push(id)
      }
    })
    // Fall back to the remove button's variant list if there were no editable cells.
    if (!ids.length) {
      const remove = row.querySelector('order-grid-row-remove[variant-ids]')
      if (remove) {
        ;(remove.getAttribute('variant-ids') || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
          .forEach((id) => ids.push(id))
      }
    }
    return ids
  }

  /** Sum of the editable quantity inputs in a row (its current on-screen total). */
  function rowQuantity(row) {
    let sum = 0
    row.querySelectorAll('input.order-grid__input').forEach((input) => {
      const n = parseInt(input.value, 10)
      if (!Number.isNaN(n)) {
        sum += n
      }
    })
    return sum
  }

  const MONEY_RE = /[\d][\d.,\s]*/
  function zeroMoney(el) {
    if (!el) {
      return
    }
    if (window.__cartZeroMoneyZero) {
      el.textContent = window.__cartZeroMoneyZero
    } else {
      el.textContent = el.textContent.replace(MONEY_RE, '0')
    }
  }

  /** Paint a grid row as a zero placeholder: every editable qty 0, row total 0, count 0. */
  function paintPlaceholder(row) {
    row.classList.add('order-grid__row--zeroed')
    row.querySelectorAll('input.order-grid__input').forEach((input) => {
      if (input.type === 'number') {
        input.value = '0'
        input.setAttribute('value', '0')
      }
    })
    const totalCell = row.querySelector('.order-grid__total-cell')
    if (totalCell) {
      const spans = totalCell.querySelectorAll('span')
      // First span is the money total; zero its amount.
      zeroMoney(spans[0])
      // Second span is the item-count label (e.g. "1 item"); force its number to 0.
      if (spans[1]) {
        spans[1].textContent = spans[1].textContent.replace(/\d[\d.,]*/, '0')
      }
    }
  }

  /** The grid element (there may be one; scope everything to it). */
  function gridEl() {
    return document.querySelector('order-grid, .order-grid')
  }

  /**
   * The row's position among its sibling rows within the same style card at record time, so the
   * placeholder can be re-inserted at its ORIGINAL slot instead of being appended to the end of the
   * card. Counts every `.order-grid__row` sibling (real or placeholder) preceding this one.
   */
  function rowIndexIn(row) {
    const card = row.closest('.order-grid__style-card')
    const scope = card || gridEl()
    if (!scope) {
      return -1
    }
    const siblings = Array.from(scope.querySelectorAll('.order-grid__row'))
    return siblings.indexOf(row)
  }

  /**
   * A key identifying the style card (group) a row lives in, so a placeholder is re-inserted into
   * the SAME card its product came from. Uses the card's "Art.no." (base SKU) text, falling back
   * to the card's index within the grid.
   */
  function styleCardKeyFor(row) {
    const card = row.closest('.order-grid__style-card')
    if (!card) {
      return null
    }
    // The base SKU line: "<Art.no. label>: N50-0300".
    const info = card.querySelector('.order-grid__style-card-info')
    if (info) {
      const skuSpan = Array.from(info.querySelectorAll('span')).find((s) => /:\s*\S/.test(s.textContent))
      if (skuSpan) {
        return `sku:${skuSpan.textContent.trim()}`
      }
    }
    const cards = Array.from(document.querySelectorAll('.order-grid__style-card'))
    return `idx:${cards.indexOf(card)}`
  }

  /** The tbody to append a placeholder to, for a given style-card key. */
  function tbodyForKey(key) {
    const cards = Array.from(document.querySelectorAll('.order-grid__style-card'))
    let card = null
    if (key && key.indexOf('sku:') === 0) {
      const skuText = key.slice(4)
      card = cards.find((c) => {
        const info = c.querySelector('.order-grid__style-card-info')
        return info && Array.from(info.querySelectorAll('span')).some((s) => s.textContent.trim() === skuText)
      })
    } else if (key && key.indexOf('idx:') === 0) {
      card = cards[parseInt(key.slice(4), 10)]
    }
    if (card) {
      return card.querySelector('tbody') || card.querySelector('table')
    }
    // Fallback: first tbody in the grid.
    const grid = gridEl()
    return grid ? grid.querySelector('tbody') || grid.querySelector('table') || grid : null
  }

  /**
   * Insert `node` into `container` at the placeholder's recorded row position, so it reappears in
   * its original slot rather than at the bottom of the card. `index` counts existing
   * `.order-grid__row` siblings; the node is placed before the row currently occupying that slot
   * (appended when the index is past the end or was never recorded).
   */
  function insertAtIndex(container, node, index) {
    if (typeof index !== 'number' || index < 0) {
      container.appendChild(node)
      return
    }
    const rows = Array.from(container.querySelectorAll('.order-grid__row'))
    const ref = rows[index]
    if (ref && ref.parentNode) {
      ref.parentNode.insertBefore(node, ref)
    } else {
      container.appendChild(node)
    }
  }

  // ---------- 1. Record on quantity→0 / clear on remove ----------

  // Capture phase so we snapshot BEFORE the grid's own change handler enqueues the update and
  // re-renders. We never preventDefault — the native remove + re-render proceed.
  document.addEventListener(
    'change',
    function (event) {
      const input = event.target
      if (!input || !input.matches || !input.matches('input.order-grid__input')) {
        return
      }
      if (inCartDrawer(input)) {
        return
      }
      const row = input.closest('.order-grid__row')
      if (!row || row.classList.contains('order-grid__row--zeroed')) {
        return
      }
      // Only snapshot when this change drives the WHOLE row to 0 (product will leave the cart).
      const rowQty = rowQuantity(row)
      debug('change on grid input; row total qty =', rowQty)
      if (rowQty === 0) {
        recordRow(row)
      }
    },
    true
  )

  // Remove button: a true removal. Drop any stored placeholder for the row's variants so it does
  // not reappear. Let the grid's own remove handler run.
  document.addEventListener(
    'click',
    function (event) {
      const btn = event.target.closest && event.target.closest('.order-grid__remove-btn')
      if (!btn || inCartDrawer(btn)) {
        return
      }
      // The style-level remove (header X) is confirmation-gated and forgets its own placeholders
      // via the 'order-grid:forget-placeholders' event below — skip it here so a cancelled prompt
      // doesn't wrongly drop stored rows in this synchronous capture phase.
      if (btn.closest('order-grid-item-remove')) {
        return
      }
      const row = btn.closest('.order-grid__row')
      if (!row) {
        return
      }
      forgetPlaceholdersForVariants(rowVariantIds(row))
      // If this was a placeholder row, remove it from the DOM immediately (it isn't a real cart
      // line, so the grid's own handler has nothing to remove server-side).
      if (row.classList.contains('order-grid__row--zeroed')) {
        event.preventDefault()
        event.stopImmediatePropagation()
        row.remove()
      }
    },
    true
  )

  // Confirmed style-level removals (and any other flow) can ask us to forget placeholders for a
  // set of variant ids so a "remove entire item" leaves no zeroed ghost rows behind.
  document.addEventListener('order-grid:forget-placeholders', function (event) {
    const ids = (event.detail && event.detail.variantIds) || []
    if (ids.length) {
      forgetPlaceholdersForVariants(ids)
    }
  })

  function recordRow(row) {
    const variantIds = rowVariantIds(row)
    if (!variantIds.length) {
      return
    }
    const clone = row.cloneNode(true)
    paintPlaceholder(clone)
    const cardKey = styleCardKeyFor(row)
    const rowIndex = rowIndexIn(row)
    debug('recording placeholder', { variantIds, cardKey, rowIndex })
    rememberPlaceholder({ variantIds, cardKey, rowIndex, html: clone.outerHTML })
  }

  // ---------- 2. Restore placeholders after every grid render ----------

  /** Variant ids present as REAL (non-placeholder) grid rows right now. */
  function variantsInGrid() {
    const ids = new Set()
    const grid = gridEl()
    if (!grid) {
      return ids
    }
    grid.querySelectorAll('.order-grid__row').forEach((row) => {
      if (row.classList.contains('order-grid__row--zeroed')) {
        return
      }
      rowVariantIds(row).forEach((id) => ids.add(String(id)))
    })
    return ids
  }

  let injecting = false

  function restorePlaceholders() {
    if (injecting) {
      return
    }
    const entries = readStore()
    if (!entries.length) {
      return
    }
    if (!gridEl()) {
      return
    }
    const present = variantsInGrid()
    debug('restore: entries =', entries.length, 'variants in grid =', [...present])
    // Placeholders already on screen, keyed by their sorted variant-id list.
    const shown = new Set(
      Array.from(document.querySelectorAll('.order-grid__row--zeroed')).map((row) =>
        rowVariantIds(row).map(String).sort().join(',')
      )
    )
    injecting = true
    try {
      const stillValid = []
      for (const entry of entries) {
        const ids = (entry.variantIds || []).map(String)
        // The product is back in the real cart (any of its variants rendered) — drop placeholder.
        if (ids.some((id) => present.has(id))) {
          continue
        }
        stillValid.push(entry)
        if (shown.has(ids.slice().sort().join(','))) {
          continue
        }
        const container = tbodyForKey(entry.cardKey)
        if (!container) {
          continue
        }
        const temp = document.createElement('template')
        temp.innerHTML = (entry.html || '').trim()
        const node = temp.content.firstElementChild
        if (!node) {
          continue
        }
        node.classList.add('order-grid__row--zeroed')
        paintPlaceholder(node)
        insertAtIndex(container, node, entry.rowIndex)
        debug('injected placeholder for', ids, 'into', entry.cardKey, 'at', entry.rowIndex)
      }
      if (stillValid.length !== entries.length) {
        writeStore(stillValid)
      }
    } finally {
      injecting = false
    }
  }

  const deferredRestore = () => setTimeout(restorePlaceholders, 0)
  document.addEventListener('cart:change', deferredRestore)
  document.addEventListener('cart:refresh', deferredRestore)
  document.documentElement.addEventListener('cart:change', deferredRestore)
  document.documentElement.addEventListener('cart:refresh', deferredRestore)

  // The grid replaces its own innerHTML on re-render; a MutationObserver on a stable ancestor
  // catches that and any other re-render path. Debounced, and guarded against our own inserts.
  let observer = null
  let scheduled = false
  function observeCart() {
    if (observer) {
      return
    }
    // IMPORTANT: the grid re-renders itself with `section.outerHTML = …`, which REPLACES the
    // whole `.shopify-section` node. Observing the section (or the grid) would leave the observer
    // bound to the detached old node and it would never fire again. Observe a stable ancestor that
    // is never replaced — the section's parent — so we keep seeing every re-render.
    const grid = gridEl()
    const section = grid && grid.closest('.shopify-section')
    const target =
      (section && section.parentElement) ||
      (grid && grid.parentElement) ||
      document.querySelector('main') ||
      document.body
    if (!target) {
      return
    }
    observer = new MutationObserver(() => {
      if (injecting || scheduled) {
        return
      }
      scheduled = true
      setTimeout(() => {
        scheduled = false
        restorePlaceholders()
      }, 0)
    })
    observer.observe(target, { childList: true, subtree: true })
  }

  function init() {
    restorePlaceholders()
    observeCart()
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init)
  } else {
    init()
  }
})()
